import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { RootState, useAppThunkDispatch } from 'state/store';
import { selectIsUserLd } from 'state/User/userSlice';
import {
  toggleDuplicateProjectModal,
  selectProjectPermissionsById,
  selectIsProjectArchived,
  toggleArchiveProjectModal,
} from 'state/ProjectsList/projectsListSlice';
import { fetchProject, setProjectId } from 'state/Project/projectSlice';
import { PROJECTS_LIST_ACTIONS } from 'utils/constants';
import UnarchiveIcon from 'assets/icons/unarchive.svg';

type QuickProjectAction =
  typeof PROJECTS_LIST_ACTIONS[keyof typeof PROJECTS_LIST_ACTIONS];

interface QuickActionsMenuItem {
  label: string;
  iconName?: string;
  iconSrc?: string;
  onSelect: () => void;
  action: QuickProjectAction;
}

interface UseQuickActionsProps {
  projectId: string;
}

export const useQuickActions = ({ projectId }: UseQuickActionsProps) => {
  const dispatch = useAppThunkDispatch();
  const permissions = useSelector((state: RootState) =>
    selectProjectPermissionsById(state, projectId)
  );
  const isProjectArchived = useSelector((state: RootState) =>
    selectIsProjectArchived(state, projectId)
  );
  const isLDUser = useSelector(selectIsUserLd);

  const openDuplicateProjectModal = useCallback(() => {
    dispatch(fetchProject(projectId));
    dispatch(toggleDuplicateProjectModal());
  }, [dispatch, projectId]);

  const openUnarchiveProjectModal = useCallback(() => {
    dispatch(fetchProject(projectId));
    dispatch(toggleArchiveProjectModal());
  }, [dispatch, projectId]);

  const canArchiveProject = useMemo(() => {
    return permissions?.canArchive ?? false;
  }, [permissions]);

  const canViewProject = useMemo(() => {
    return permissions?.canView ?? false;
  }, [permissions]);

  const openProjectPanel = useCallback(() => {
    dispatch(setProjectId(projectId));
  }, [dispatch, projectId]);

  const quickActionsMenuItems: QuickActionsMenuItem[] = useMemo(() => {
    if (!isLDUser) {
      return [];
    }

    return [
      {
        label: intl.get('DUPLICATE_PROJECT_MODAL.TITLE'),
        iconName: 'copy-outline',
        onSelect: openDuplicateProjectModal,
        action: PROJECTS_LIST_ACTIONS.DUPLICATE,
      },
      ...(canArchiveProject && isProjectArchived
        ? [
            {
              label: intl.get('ARCHIVE_PROJECT_MODAL.TITLE', {
                action: intl.get('UNARCHIVE'),
              }),
              iconSrc: UnarchiveIcon,
              onSelect: openUnarchiveProjectModal,
              action: PROJECTS_LIST_ACTIONS.UNARCHIVE,
            },
          ]
        : []),
      ...(canArchiveProject && !isProjectArchived
        ? [
            {
              label: intl.get('ARCHIVE_PROJECT_MODAL.TITLE', {
                action: intl.get('ARCHIVE'),
              }),
              iconName: 'archive',
              onSelect: openUnarchiveProjectModal,
              action: PROJECTS_LIST_ACTIONS.ARCHIVE,
            },
          ]
        : []),
    ];
  }, [
    isLDUser,
    openDuplicateProjectModal,
    openUnarchiveProjectModal,
    isProjectArchived,
    canArchiveProject,
  ]);

  return {
    quickActionsMenuItems,
    canViewProject,
    openProjectPanel,
  };
};
